import { useState } from 'react';

import { ChakraFlex, ChakraText, EquipUITheme } from '@equip.health/ui';

import EquipScaleActionBar from '~/components/maud/patients/profile/clinical-profile/equip-scale/EquipScaleActionBar';
import ClinicalMeasurements from '~/components/maud/patients/profile/clinical-profile/clinical-measurement';
import OrderEquipScale from '~/components/maud/patients/profile/clinical-profile/equip-scale/OrderEquipScale';
import { ClinicalProfileDiagnosisSection } from '~/components/maud/patients/profile/clinical-profile/diagnosis/ClinicalProfileDiagnosisSection';
import ClinicalProfileDevicesSection from '~/components/maud/patients/profile/clinical-profile/equip-scale/ClinicalProfileDevicesSection';
import {
  OrderEquipScaleFrom,
  ReorderContext,
} from '~/lib/constants/patients.constants';
import useEquipScaleWarnings from '~/lib/hooks/useEquipScaleWarnings';
import { shouldShowActionBar } from '~/lib/util/clinicalProfile.util';
import { getAgeFromDOB } from '~/lib/util/patient.util';

const { h5 } = EquipUITheme.typography;
const PATIENT_DIAGNOSIS_SECTION = 'patient__diagnosis-section';

interface PatientClinicalProfileTabProps {
  assessmentList: SurveyResponse[];
  patient: Patient;
  patientId: string;
  patientPreferredFullName: string;
}

const PatientClinicalProfileTab = ({
  assessmentList,
  patientId,
  patient,
  patientPreferredFullName,
}: PatientClinicalProfileTabProps) => {
  const [showOrderEquipScaleFrom, setShowOrderEquipScaleFrom] =
    useState<OrderEquipScaleFrom>(OrderEquipScaleFrom.NONE);
  const [reorderContext, setReorderContext] = useState<ReorderContext | null>(
    null,
  );

  const age = patient.dateOfBirth
    ? Number(getAgeFromDOB(patient.dateOfBirth, 'yyyy-LL-dd'))
    : 0;

  const patientName = patient?.chosenName || patient?.firstName;
  const showActionBar = shouldShowActionBar(age);
  const { warnings } = useEquipScaleWarnings(patientId, age);

  const shouldShowDevice = (): boolean => {
    return age >= 18 || (age < 18 && Boolean(patient?.supports?.[0]?.isProxy));
  };

  const handleOrderScale = (
    from: OrderEquipScaleFrom,
    context?: ReorderContext,
  ) => {
    setReorderContext(context ?? null);
    setShowOrderEquipScaleFrom(from);
  };

  const handleOrderScaleClose = () => {
    setReorderContext(null);
    setShowOrderEquipScaleFrom(OrderEquipScaleFrom.NONE);
  };

  return (
    <ChakraFlex
      flexDirection="column"
      gap="24px"
      marginBottom="24px"
      marginTop="12px"
    >
      {showActionBar && (
        <EquipScaleActionBar
          handleOrderScale={handleOrderScale}
          patientId={patientId}
          patientName={patientName}
        />
      )}
      <ClinicalMeasurements
        patient={patient}
        patientId={patientId}
        patientPreferredFullName={patientPreferredFullName}
      />
      {showOrderEquipScaleFrom && (
        <OrderEquipScale
          isReorder={reorderContext?.isReorder}
          onClose={handleOrderScaleClose}
          openOrderEquipScaleFrom={showOrderEquipScaleFrom}
          patient={patient}
          reorderReasonCode={reorderContext?.reorderReasonCode}
        />
      )}
      <ChakraFlex>
        <ChakraText
          {...h5}
          id={`${PATIENT_DIAGNOSIS_SECTION}__header`}
          lineHeight="42px"
        >
          Medical history
        </ChakraText>
      </ChakraFlex>
      <ClinicalProfileDiagnosisSection
        assessmentList={assessmentList}
        patientId={patientId}
      />
      {shouldShowDevice() && (
        <ClinicalProfileDevicesSection
          handleOrderScale={handleOrderScale}
          patientId={patientId}
          patientName={patientName}
          warnings={warnings}
        />
      )}
    </ChakraFlex>
  );
};

export default PatientClinicalProfileTab;
